import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import {
  LayoutDashboard,
  Bot,
  Layout as LayoutIcon,
  Server,
  ArrowLeftRight,
  CheckSquare,
  Star,
  Shield,
  TrendingUp,
  Scale,
  Globe,
  Activity,
  Settings,
  BookOpen,
  Menu,
  X,
  LogOut,
  ChevronRight,
  Bell
} from 'lucide-react';

const menuItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/notifications', icon: Bell, label: 'Notifications' },
  { path: '/network', icon: Globe, label: 'Network Status', adminOnly: true },
  { path: '/bots', icon: Bot, label: 'My Bots' },
  { path: '/templates', icon: LayoutIcon, label: 'Template' },
  { path: '/services', icon: Server, label: 'Services' },
  { path: '/transactions', icon: ArrowLeftRight, label: 'Transactions' },
  { path: '/approvals', icon: CheckSquare, label: 'Approvals', adminOnly: true },
  { path: '/reputation', icon: Star, label: 'Reputation', adminOnly: true },
  { path: '/aiic', icon: Shield, label: 'AIIC' },
  { path: '/stock-market', icon: TrendingUp, label: 'Stock Market' },
  { path: '/wiki', icon: BookOpen, label: 'Wiki' },
  { path: '/ethics', icon: Scale, label: 'Ethics', adminOnly: true },
  { path: '/system', icon: Activity, label: 'System Activity', adminOnly: true },
  { path: '/settings', icon: Settings, label: 'Settings' }
];

export function Layout({ children }) {
  const { user, logout, refreshUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAdmin = user?.is_superuser === true;
  const visibleMenuItems = menuItems.filter((item) => !item.adminOnly || isAdmin);

  useEffect(() => {
    let isMounted = true;

    const refreshIfVisible = () => {
      if (!isMounted) return;
      if (document.visibilityState !== 'visible') return;
      refreshUser();
    };

    refreshIfVisible();
    const interval = setInterval(refreshIfVisible, 60000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshIfVisible();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      isMounted = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);
  const [notificationCount, setNotificationCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;
    const fetchNotifications = async () => {
      if (!isMounted || document.visibilityState !== 'visible') {
        return;
      }
      try {
        const response = await api.get('/notifications');
        if (!isMounted) return;
        setNotificationCount(response.data?.notifications?.length ?? 0);
      } catch (error) {
        if (!isMounted) return;
        setNotificationCount(0);
      }
    };
    if (user) {
      fetchNotifications();
      // Poll every 30 seconds for notification updates
      const interval = setInterval(fetchNotifications, 30000);
      const handleVisibility = () => {
        if (document.visibilityState === 'visible') {
          fetchNotifications();
        }
      };
      document.addEventListener('visibilitychange', handleVisibility);
      return () => {
        isMounted = false;
        clearInterval(interval);
        document.removeEventListener('visibilitychange', handleVisibility);
      };
    }
    return () => {
      isMounted = false;
    };
  }, [user]);

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-cyan-500 focus:text-white focus:rounded-lg focus:shadow-lg transition-all"
      >
        Skip to main content
      </a>
      <div className="min-h-screen bg-slate-950 flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-slate-900 border-r border-slate-800
          transform transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
      >
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-800/70 flex items-center justify-center overflow-hidden">
              <img
                src="/logo.png"
                alt="Market4Bot"
                loading="eager"
                width="40"
                height="40"
                className="w-10 h-10 object-cover"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Market4Bot</h1>
              <p className="text-xs text-gray-500">AI Agent Marketgame</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {visibleMenuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-lg
                transition-all duration-200
                ${isActive
                  ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30'
                  : 'text-gray-400 hover:bg-slate-800 hover:text-white'
                }
              `}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
              {item.path === '/notifications' && notificationCount > 0 && (
                <span className="ml-auto px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                  {notificationCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <span className="text-white font-semibold">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
            </div>
            <button
              onClick={logout}
              aria-label="Log out"
              className="p-2 text-gray-400 hover:text-red-400 transition-colors"
              title="Log out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
          <div className="flex items-center justify-between px-4 py-4 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
              className="lg:hidden p-2 text-gray-400 hover:text-white"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Breadcrumb */}
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-500">
              <span>Market4Bot</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">
                {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
              </span>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              <NavLink
                to="/notifications"
                className="relative p-2 text-gray-400 hover:text-white transition-colors"
              >
                <Bell className="w-5 h-5" />
                {notificationCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </NavLink>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-300">{user?.balance?.toLocaleString() || 0} SERV</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main id="main-content" className="flex-1 overflow-auto p-4 lg:p-8" tabIndex="-1">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
    </>
  );
}
